import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { buildMonitorLink, getDashboardById, getFirstDashboard, getFirstDashboardId } from '@Helpers';

import { ErrorMessage, Loading, WaitingScreen } from '@Components';
import { useSidebar } from '@Components/ui-v2/sidebar';
import { useFetchData } from '@CustomHooks';
import { ACCOUNT_READY, DashboardGroup, DashboardItem } from '@Models';
import { bopsApi } from '@Network';
import { RootState } from '@Store';
import { screenSize } from '@Themes';
import config from '../../config';
import Monitor from './monitor';

interface MonitorContainerProps {
  dashboards?: DashboardItem[] | DashboardGroup[];
  hideBookmark?: boolean;
  dataReady?: boolean;
  pendingManualTaskType?: string;
  status?: string;
}

interface MonitorContentProps {
  dashboards?: DashboardItem[] | DashboardGroup[];
  hideBookmark?: boolean;
}

/**
 * Renders the Monitor experience for a data-ready workspace. The outer
 * MonitorContainer handles the provisioning redirect; this component is
 * mounted only when the workspace is past that gate, so it doesn't need to
 * know about `dataReady`.
 */
const MonitorContent = ({ dashboards, hideBookmark }: MonitorContentProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const { monitorId } = params;

  const location = useLocation();
  const { sheet } = location.state || {};
  const { state, setOpen } = useSidebar();
  const collapsed = state === 'collapsed';
  const { t } = useTranslation();

  const hasMultipleDashboards = dashboards && dashboards.length > 0;
  const firstDashboardId = getFirstDashboardId(dashboards);
  const firstDashboard = getFirstDashboard(dashboards);
  const currentDashboard = getDashboardById(dashboards, monitorId);
  const currentDashboardId = currentDashboard?.id;
  const retailer = currentDashboard?.retailer || null;

  useEffect(() => {
    if (!hasMultipleDashboards) {
      if (location.pathname.includes('monitor') && location.pathname !== '/monitor') {
        navigate('/monitor');
      }
      return;
    }

    if (location.pathname === '/monitor' && firstDashboard) {
      navigate(buildMonitorLink(firstDashboard));
      return;
    }

    // When the URL id doesn't belong to the current client's dashboards
    // (typical after a client switch leaves a stale id in the URL) the fetch
    // would 404 — land on the first valid dashboard instead.
    if (monitorId && !currentDashboardId && firstDashboard) {
      navigate(buildMonitorLink(firstDashboard));
    }
  }, [hasMultipleDashboards, firstDashboardId, currentDashboardId, location.pathname]);

  // `dashboards` is already loaded by the time this mounts (the outer layout
  // gates on the config fetch), but `monitorId` from the URL is still
  // undefined on the very first render of a bare `/monitor` visit — the
  // redirect below to the first dashboard's URL only takes effect on the
  // next render. Falling back to `firstDashboardId` here means this fetch
  // targets the right dashboard from that first render instead of racing an
  // `undefined`-id request (a real 404) against the corrected one.
  const effectiveDashboardId = monitorId || firstDashboardId;
  const fetchKey = effectiveDashboardId || '';
  const { data, isLoading, error } = useFetchData(() => bopsApi.getDashboard(effectiveDashboardId), fetchKey);

  useEffect(() => {
    if (collapsed || isLoading) return;
    if (window.innerWidth > screenSize.desktop) return;
    setOpen(false);
  }, [isLoading]);

  if (isLoading) return <Loading title={t('Monitor.Loading')} />;
  // Defense-in-depth fallback: should rarely fire once the config-level
  // pendingManualTaskType gate in MonitorContainer is in place. Covers e.g.
  // a legacy client with zero dashboards and no matching supplier_manual_tasks
  // row. A plain 404 page reads as broken to a paying supplier — this is a
  // much safer default than NotFound.
  if (error?.response?.status === 404) return <WaitingScreen />;
  if (error || !data) return <ErrorMessage internalMessage={`Error getting dashboard id ${error}`} />;

  return (
    <Monitor
      dashboardUrl={String(data)}
      sheet={sheet}
      hideBookmark={hideBookmark}
      retailer={retailer}
      dashboardName={currentDashboard?.name}
      dashboardId={effectiveDashboardId}
      dashboardQuicksightId={currentDashboard?.quicksightId}
    />
  );
};

/**
 * Provisioning has its own route at /onboarding. Bail to it before mounting
 * any Monitor hooks/logic so the URL reflects the workspace state and the
 * inner component never has to reason about provisioning. `dataReady` is a
 * GS1-only field, so the redirect is scoped to that flavor too — non-GS1
 * clients never have a "provisioning" state in the first place.
 *
 * `pendingManualTaskType` is the Logyca equivalent: a whole-workspace gate,
 * independent of which dashboard id is in the URL, checked before any
 * per-dashboard fetch happens at all — a client mid-plan-change may still
 * have dashboards from their old plan, so this can't wait for a 404.
 *
 * `status` covers the remaining gap: a Logyca supplier whose account isn't
 * `ACCOUNT_READY` yet (CREATION_IN_PROGRESS, ACCOUNT_WITHOUT_DATA,
 * ACCOUNT_PENDING_REFRESH) but has no matching supplier_manual_tasks row
 * would otherwise fall through to MonitorContent, whose dashboard fetch
 * fails for a reason other than a clean 404 and renders the generic 500
 * ErrorMessage page instead of a friendly waiting screen.
 */
const MonitorContainer = ({
  dashboards,
  hideBookmark,
  dataReady,
  pendingManualTaskType,
  status,
}: MonitorContainerProps) => {
  if (config.isGs1Flavor && dataReady === false) {
    return <Navigate to="/onboarding" replace />;
  }
  if (config.isLogycaFlavor && pendingManualTaskType) {
    return <WaitingScreen taskType={pendingManualTaskType} />;
  }
  if (config.isLogycaFlavor && status && status !== ACCOUNT_READY) {
    return <WaitingScreen />;
  }
  return <MonitorContent dashboards={dashboards} hideBookmark={hideBookmark} />;
};

const mapStateToProps = (state: RootState) => {
  const { dashboards, hideBookmark, dataReady, pendingManualTaskType, status } = state?.client?.configuration || {};
  return { dashboards, hideBookmark, dataReady, pendingManualTaskType, status };
};

export default connect(mapStateToProps)(MonitorContainer);
